import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { memo } from "react";
import { useBszFinderContext } from "../../../context/bszContext";
import { useAppContext } from "../../../../../2_ksbHub/context/appContext";

import RoomActions from "./RoomActions";

/**
 * =========================================================
 * ✅ ROOM CARD (POLISHED WITH DRAG HANDLE)
 * =========================================================
 */
function RoomCard({
  room,
  onClick,
  onEdit,
  onDelete,
  dragProps,
  isDraggable,
  alwaysGreen,
}) {
  const theme = useTheme();
  const { isMobile, isTeacherView } = useAppContext();
  const { isBszAdmin } = useBszFinderContext();

  const { name, free, capacity } = room;

  /**
   * =========================================================
   * ✅ STATUS COLOR
   * =========================================================
   */
  let bgColor;
  let borderColor;

  if (free === 0 && !alwaysGreen) {
    bgColor = theme.palette.levels.red;
    borderColor = theme.palette.error.main;
  } else if (free < capacity * 0.2 && !alwaysGreen) {
    bgColor = theme.palette.levels.yellow;
    borderColor = theme.palette.warning.main;
  } else {
    bgColor = theme.palette.levels.green;
    borderColor = theme.palette.levels.lightGreen;
  }

  /**
   * =========================================================
   * ✅ RENDER
   * =========================================================
   */
  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        borderRadius: 2,
        overflow: "hidden",
        transition: "transform 0.2s ease",

        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `${theme.palette.primary.main}33 0px 4px 10px`,
        },
      }}
    >
      {/* ===================================================== */}
      {/* ✅ DRAG HANDLE (ADMIN ONLY) */}
      {/* ===================================================== */}
      {isDraggable && (
        <Box
          {...dragProps}
          onClick={(e) => e.stopPropagation()} // ✅ prevent dialog
          sx={{
            width: "10px",
            backgroundColor: borderColor,
            flexShrink: 0,
            cursor: "grab",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,

            transition: "all 0.2s ease",

            "&:hover": {
              width: "14px",
              opacity: 0.9,
            },

            "&:active": {
              cursor: "grabbing",
            },

            /**
             * ✅ subtle grip indicator
             */
            "&::  after": {
              content: '"⋮⋮"',
              fontSize: "10px",
              color: "rgba(255,255,255,0.6)",
              lineHeight: 1,
            },
          }}
        />
      )}

      {/* ===================================================== */}
      {/* ✅ CONTENT */}
      {/* ===================================================== */}
      <Box
        onClick={(e) => {
          if (e.defaultPrevented) return;
          onClick(room);
        }}
        sx={{
          flex: 1,
          p: 2,
          backgroundColor: bgColor,

          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",

          // minHeight: "90px",
          width: isMobile
            ? "146px"
            : isBszAdmin && !isTeacherView
              ? "220px"
              : "150px",
          cursor: "pointer",

          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,

          /**
           * ✅ nicer contrast
           */
          color: "#fff",

          /**
           * ✅ subtle inner contrast
           */
          boxShadow: "inset 0 0 3px rgba(0,0,0,0.1)",
        }}
      >
        {/* ✅ ROOM NAME */}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            letterSpacing: "0.03em",
          }}
        >
          {name}
        </Typography>

        {/* ✅ OCCUPANCY */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            opacity: free === 0 ? 0.7 : 0.95,
          }}
        >
          {free} frei / {capacity}
        </Typography>

        {/* ✅ ACTIONS */}
        <RoomActions
          room={room}
          isAdmin={!isTeacherView && isBszAdmin}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </Box>
    </Box>
  );
}

export default memo(RoomCard);
